import React, { useLayoutEffect, useRef, useState, useMemo } from 'react';
import { Link, useSearchParams } from 'react-router';
import { useMount } from 'ahooks';

import {
    useStyles,
    Dialog,
    Button,
    useAppDisplayModeContext,
} from '@archipro-design/aria';
import {
    CloseLine,
    ArrowLeftSLine,
    ArrowRightSLine,
} from '@archipro-design/icons';
import type { MagazinePaginationState } from '~/modules/magazine/type';
import * as S from './MagazineViewerModal.style';

const hasWindow =
    typeof window === 'object' && window !== null && window.self === window;

interface NewsletterDimensions {
    height: number;
    width: string;
}

interface HubspotMessageEventData {
    type: string;
    value: string | NewsletterDimensions;
}

const getDimensions = (
    source: string | NewsletterDimensions,
    fallbackWidth: number,
    fallbackHeight: number,
): { width: number; height: number } => {
    if (typeof source === 'string') {
        return {
            width: fallbackWidth,
            height: fallbackHeight,
        };
    }

    const width = source.width ? parseInt(source.width) : fallbackWidth,
        height = source.height ? source.height : fallbackHeight;

    return {
        width: !isNaN(width) && width > fallbackWidth ? width : fallbackWidth,
        height: height > fallbackHeight ? height : fallbackHeight,
    };
};

export const MagazineViewerModal = ({
    iframeLink,
    toggleModal,
    isOpen,
    urlSegment,
    paginationState,
}: {
    iframeLink: string;
    toggleModal: () => void;
    isOpen: boolean;
    urlSegment: string;
    paginationState: MagazinePaginationState;
}) => {
    const fallbackWidth = 1920,
        fallbackHeight = 1080;

    const [iframeWidth, setIframeWidth] = useState(fallbackWidth);
    const [iframeHeight, setIframeHeight] = useState(fallbackHeight);
    const [scalingFactor, setScalingFactor] = useState(1);
    const [dimensionsDetermined, setDimensionsDetermined] = useState(false);
    const initialWidth = useRef<number>(fallbackWidth);
    const initialHeight = useRef<number>(fallbackHeight);

    if (hasWindow) {
        initialWidth.current = window.innerWidth;
        initialHeight.current = window.innerHeight;
    }

    const { css } = useStyles({
        scalingFactor,
        iframeWidth,
        iframeHeight,
        dimensionsDetermined,
    });

    const [searchParams] = useSearchParams();
    const [prevLink, nextLink] = useMemo(() => {
        const current = paginationState.list.findIndex(
            (url) => url === urlSegment,
        );
        if (current === -1) return ['', ''];

        const prev = paginationState.list[current - 1];
        const next = paginationState.list[current + 1];
        return [
            prev ? `${prev}?${searchParams.toString()}` : '',
            next ? `${next}?${searchParams.toString()}` : '',
        ];
    }, [urlSegment, paginationState, searchParams]);

    const iframeRef = useRef(null);
    const prevBtnRef = useRef(null);
    const nextBtnRef = useRef(null);

    const { desktop, mobile } = useAppDisplayModeContext();

    useLayoutEffect(() => {
        const handleNewsletterWidth = (
            message: MessageEvent<HubspotMessageEventData>,
        ) => {
            const dims =
                message instanceof MessageEvent
                    ? getDimensions(
                          message.data?.value ?? '',
                          fallbackWidth,
                          initialHeight.current,
                      )
                    : getDimensions('', fallbackWidth, initialHeight.current);

            const hubspotTableWidth = 850;
            setScalingFactor(
                window.innerWidth < hubspotTableWidth
                    ? window.innerWidth / hubspotTableWidth
                    : 1,
            );
            setIframeWidth(dims.width + 20);
            setIframeHeight(dims.height);
            setDimensionsDetermined(true);
        };

        window.addEventListener('message', handleNewsletterWidth);
        return () => {
            window.removeEventListener('message', handleNewsletterWidth);
        };
    }, [mobile, fallbackWidth, initialHeight]);

    useMount(() => {
        const handleKeyDown = (
            message: MessageEvent<HubspotMessageEventData>,
        ) => {
            if (!message.data) {
                return;
            }

            if (
                message.data.type !== 'keydown' ||
                message.data.value !== 'Escape'
            ) {
                return;
            }

            toggleModal();
        };
        window.addEventListener('message', handleKeyDown);

        return () => {
            window.removeEventListener('message', handleKeyDown);
        };
    });

    const handleEscapePressed = (e: React.KeyboardEvent) => {
        if (e.key !== 'Escape') {
            return;
        }
        e.stopPropagation();
        toggleModal();
    };

    return (
        <Dialog
            open={isOpen}
            trapFocus={true}
            onKeyDown={handleEscapePressed}
            header={{
                style: { padding: '0', margin: '0' },
            }}
            content={{
                styles: { padding: '0', margin: '0' },
                content: (
                    <>
                        <Button
                            className={css(S.CloseButton)}
                            onClick={(event) => {
                                event.stopPropagation();
                                toggleModal();
                            }}
                            size={24}
                            color="white"
                            variables={{
                                buttonPadding: desktop ? 16 : 12,
                                iconPadding: 0,
                                buttonRadius: '50%',
                            }}
                            design={{
                                position: 'absolute',
                            }}
                            icon={<CloseLine />}
                        />
                        {desktop && (
                            <div className={css(S.MagazineNavs)}>
                                {prevLink ? (
                                    <Button
                                        ref={prevBtnRef}
                                        icon={<ArrowLeftSLine />}
                                        variables={{
                                            iconPadding: 0,
                                        }}
                                        as={Link}
                                        to={`/digital-magazine/${prevLink}`}
                                        preventScrollReset={true}
                                        prefetch={'render'}
                                    ></Button>
                                ) : (
                                    <div />
                                )}
                                {nextLink && (
                                    <Button
                                        ref={nextBtnRef}
                                        icon={<ArrowRightSLine />}
                                        variables={{
                                            iconPadding: 0,
                                        }}
                                        as={Link}
                                        to={`/digital-magazine/${nextLink}`}
                                        preventScrollReset={true}
                                        prefetch={'render'}
                                    ></Button>
                                )}
                            </div>
                        )}
                        <div
                            className={css(S.MagazineIframeWrapper)}
                            onKeyDown={handleEscapePressed}
                        >
                            <iframe
                                className={css(S.MagazineIframe)}
                                ref={iframeRef}
                                src={iframeLink}
                                title="Magazine"
                                frameBorder="0"
                                allowFullScreen={true}
                                scrolling="yes"
                            />
                        </div>
                    </>
                ),
                className: css(S.MagazineWrapper),
                style: S.MagazineWrapperContentStyle,
            }}
            style={S.MagazineDialogStyle}
            footer={{
                style: { padding: '0' },
            }}
        />
    );
};
